// src/services/apiClient.ts
import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import authService from './authService';
import { shouldBypassAuth } from '../config/devMode';

/**
 * Public routes the app exposes WITHOUT requiring authentication.
 *
 * On 401 (e.g. a stale token in localStorage from a previous session),
 * the apiClient response interceptor would normally hard-navigate to
 * /login. That's the right behaviour for protected pages — but for
 * routes a convention attendee or marketing visitor can legitimately
 * reach without auth, the redirect would silently destroy their
 * intended flow.
 *
 * Most impactful example: someone scans a QR coupon card and lands on
 * `/redeem/LXIA-...`. If they have ANY background 401 (the auth hook
 * trying to validate a stale token, the pending-reward banner probing
 * /coupons/my because useAuth incorrectly reported authed), the page
 * navigates away to /login and the coupon redemption flow is lost.
 *
 * Keep this list small + focused on public-entry routes. Anything
 * here will quietly clear stale tokens on 401 instead of redirecting.
 *
 * Match is `startsWith` on `window.location.pathname`, so subroutes
 * like `/redeem/LXIA-...` and `/reset-password/<token>` are covered.
 */
const PUBLIC_FLOW_ROUTES: ReadonlyArray<string> = [
  '/redeem',          // convention coupon redemption — must never bounce
  '/login',           // already on login — redundant redirect would loop
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/pricing',         // public pricing page; auth is optional
  // NOT /gallery — /gallery is public but /gallery/submit and
  // /gallery/:id/edit are authed. Bouncing to /login from the authed
  // sub-routes is the correct behaviour.
];

/**
 * Decide whether a 401 should silently clear tokens (true) or
 * hard-navigate to /login (false). Exported for unit-testing.
 */
export function shouldSuppressLoginRedirect(pathname: string): boolean {
  return PUBLIC_FLOW_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'));
}

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to attach auth token
apiClient.interceptors.request.use(
  (config) => {
    const tokens = authService.getTokens();
    
    if (tokens?.accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // If error is 401 Unauthorized and we have an access token
    if (error.response?.status === 401 && !originalRequest?.headers?.['X-Retry']) {
      try {
        // Try to refresh the token
        await authService.refreshToken();
        
        // Clone the original request
        const newRequest = { ...originalRequest };
        
        // Mark as retried to prevent infinite loop
        if (newRequest.headers) {
          newRequest.headers['X-Retry'] = 'true';
          
          // Update with new token
          const tokens = authService.getTokens();
          if (tokens?.accessToken) {
            newRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          }
        }
        
        // Retry the original request with new token
        return apiClient(newRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        // But skip redirect in dev bypass mode
        if (!shouldBypassAuth()) {
          authService.logout();

          // Redirect to login — UNLESS the user is currently on a
          // public-flow route (e.g. /redeem, /pricing). For those, a
          // bounce would silently destroy their intended journey
          // (most importantly: a convention attendee scanning a QR
          // coupon card with a stale token in localStorage). Just
          // clear tokens and let the page render anonymously; any
          // surface that genuinely needs auth (the Claim button on
          // /redeem) will route the user through sign-up itself.
          if (
            typeof window !== 'undefined' &&
            !shouldSuppressLoginRedirect(window.location.pathname)
          ) {
            window.location.href = '/login';
          }
        } else {
          console.warn('[DevMode] API 401 error ignored - auth bypass enabled');
        }

        return Promise.reject(refreshError);
      }
    }
    
    // If 404 Not Found
    if (error.response?.status === 404) {
      console.warn(`Route not found: ${originalRequest?.url}`);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;